import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import './StopWatch.css';
import Timer from '../Timer/Timer';
import useWakeLock from '../../utils/useWakeLock';

// How often the display refreshes while running. The timer only shows tenths
// of a second (100ms granularity), so refreshing every 50ms is smooth without
// being wasteful. Accuracy never depends on this value — elapsed time is
// always derived from timestamps.
const TICK_MS = 50;

const STATUS = {
  IDLE: 'idle',
  RUNNING: 'running',
  PAUSED: 'paused',
};

const LABELS = {
  [STATUS.IDLE]: 'Start stopwatch',
  [STATUS.RUNNING]: 'Stop stopwatch',
  [STATUS.PAUSED]: 'Reset stopwatch',
};

export default function StopWatch() {
  // The whole screen is a stopwatch; letting it dim and lock mid-run is the one
  // thing it must not do. Keyed off the page being visible, not off the run —
  // see `useWakeLock`.
  useWakeLock();

  const [status, setStatus] = useState(STATUS.IDLE);
  const [time, setTime] = useState(0);

  // Milliseconds banked from previous run segments (before the current start).
  const accumulatedRef = useRef(0);
  // Timestamp (Date.now) at which the current running segment began.
  const startRef = useRef(0);
  // Latest status, readable from stable callbacks without re-subscribing.
  const statusRef = useRef(status);
  statusRef.current = status;

  const start = useCallback(() => {
    accumulatedRef.current = 0;
    startRef.current = Date.now();
    setTime(0);
    setStatus(STATUS.RUNNING);
  }, []);

  const pause = useCallback(() => {
    accumulatedRef.current += Date.now() - startRef.current;
    setTime(accumulatedRef.current);
    setStatus(STATUS.PAUSED);
  }, []);

  const reset = useCallback(() => {
    accumulatedRef.current = 0;
    startRef.current = 0;
    setTime(0);
    setStatus(STATUS.IDLE);
  }, []);

  // The single control cycles start -> stop -> reset -> start ..., like a
  // classic single-button stopwatch. There is no resume: once stopped, the
  // next press clears the run instead of continuing it.
  const cycle = useCallback(() => {
    if (statusRef.current === STATUS.RUNNING) pause();
    else if (statusRef.current === STATUS.PAUSED) reset();
    else start();
  }, [pause, reset, start]);

  // Drive the display while running. Re-created only when the run state
  // flips, never on every tick.
  useEffect(() => {
    if (status !== STATUS.RUNNING) return undefined;
    const id = setInterval(() => {
      setTime(accumulatedRef.current + (Date.now() - startRef.current));
    }, TICK_MS);
    return () => clearInterval(id);
  }, [status]);

  // Global keyboard shortcuts, subscribed exactly once (cycle/reset are
  // stable): Space/Enter advance the cycle, R/Escape jump straight to reset
  // from any state. When a <button> is focused it handles Space/Enter
  // natively, so we defer to it to avoid a double activation.
  useEffect(() => {
    const onKeyDown = (event) => {
      const isCycle = event.code === 'Space' || event.key === ' ' || event.key === 'Enter';
      const isReset = event.code === 'KeyR' || event.key === 'r' || event.key === 'Escape';
      const focusOnButton = document.activeElement?.tagName === 'BUTTON';

      if (isCycle) {
        if (focusOnButton) return; // let the focused button activate natively
        event.preventDefault();
        cycle();
      } else if (isReset) {
        event.preventDefault();
        reset();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [cycle, reset]);

  return (
    <div className="stop-watch">
      <button
        type="button"
        className="tap-area"
        onClick={cycle}
        aria-label={LABELS[status]}
        aria-keyshortcuts="Space Enter"
      >
        <Timer time={time} />
      </button>
    </div>
  );
}
